import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';

import toast from 'components/partials/toast/toast';

import {
  useAidModel,
  useCreateAidModel,
  useUpdateAidModel,
  useAidSourceTables,
  useAidModelDataset,
  useCreateAidModelDataset,
} from './use-aid-model-details';
import { useRenameAidModel } from '../../hooks/use-aid-models';

import { useCurrentUser } from 'hooks/api/use-current-user';

import { handleAidModelError } from '../utils/aid-model-error-handler';

import {
  AID_STUDIO_STEP,
  DATASET_STATUSES,
  DEFAULT_ENTRY_TERM_YEAR,
  MODEL_SAVED_TOAST_TITLE,
} from '../aid-studio-details.constants';

import type { AidModelDto } from 'api/generated/schemas/aidModelDto';
import type { AidModelDatasetDto } from 'api/generated/schemas/aidModelDatasetDto';
import type { AidModelUpdateRequestEnrollmentType } from 'api/generated/schemas/aidModelUpdateRequestEnrollmentType';

interface UseAidStudioFormParams {
  clientId: string | number;
  modelId: string | number | undefined;
  canGetSourceTables?: boolean;
  isUserView?: boolean;
}

export interface AidStudioFormValues {
  name: string;
  description: string;
  entryTerm: number | null;
  enrollmentType: string;
  sourceTableName: string;
}

const DATASET_POLL_INTERVAL = 20000;
const DATASET_TOAST_ID = 'dataset-creation-progress';

const DEFAULT_VALUES: AidStudioFormValues = {
  name: '',
  description: '',
  entryTerm: DEFAULT_ENTRY_TERM_YEAR,
  enrollmentType: 'FIRST_YEAR',
  sourceTableName: '',
};

export const useAidStudioForm = ({
  clientId,
  modelId,
  canGetSourceTables = true,
  isUserView,
}: UseAidStudioFormParams) => {
  const navigate = useNavigate();
  const isNewModel = !modelId;

  const aidStudioBasePath = isUserView
    ? '/aid-studio'
    : `/admin/clients/${clientId}/aid-studio`;

  const [isSaving, setIsSaving] = useState(false);
  const [recreateDataset, setRecreateDataset] = useState(false);
  const [isDatasetPolling, setIsDatasetPolling] = useState(false);
  const isFormInitializedRef = useRef(false);
  const datasetFailureSurfacedRef = useRef(false);

  const currentUser = useCurrentUser();
  const userId = currentUser?.data?.oktaUserId;

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    getValues,
    formState: { errors, isValid, isDirty },
  } = useForm<AidStudioFormValues>({
    mode: 'onChange',
    defaultValues: DEFAULT_VALUES,
  });

  const aidModelQuery = useAidModel({
    userId: userId ?? '',
    clientId,
    modelId: modelId ?? '',
    enabled: !isNewModel,
  });
  // TODO(ts-migration): getAidModel returns `unknown`; narrow when the api module is typed
  const modelData = aidModelQuery.data as AidModelDto | undefined;
  const modelLoading = aidModelQuery.isLoading;
  const modelError = aidModelQuery.isError;
  const modelErrorMessage =
    (aidModelQuery.error as { data?: { detail?: string } })?.data?.detail ||
    'Failed to load aid model';

  const sourceTablesQuery = useAidSourceTables({
    userId: userId ?? '',
    enabled: canGetSourceTables && !!userId,
  });
  const sourceTables = sourceTablesQuery.data as { name: string }[] | undefined;
  const sourceTablesLoading = sourceTablesQuery.isLoading;
  const sourceTablesFetching = sourceTablesQuery.isFetching;

  const datasetQuery = useAidModelDataset({
    userId: userId ?? '',
    modelId: modelId ?? '',
    enabled: !isNewModel && !!modelId && !!userId,
    refetchInterval: isDatasetPolling ? DATASET_POLL_INTERVAL : false,
  });
  // TODO(ts-migration): getAidModelDataset returns `unknown`; narrow when the api module is typed
  const datasetData = datasetQuery.data as AidModelDatasetDto | undefined;

  useEffect(() => {
    const status = datasetData?.status;

    if (status === DATASET_STATUSES.IN_PROGRESS) {
      datasetFailureSurfacedRef.current = false;

      if (!isDatasetPolling) {
        setIsDatasetPolling(true);
        toast.info({
          title: 'Dataset recreation started',
          message: `We'll notify you once it's ready`,
          toastId: DATASET_TOAST_ID,
          autoClose: 5000,
        });
      }

      return;
    }

    if (status === DATASET_STATUSES.FAILED) {
      if (!datasetFailureSurfacedRef.current) {
        if (isDatasetPolling) {
          toast.dismiss(DATASET_TOAST_ID);
        }

        toast.error({
          title: 'Dataset creation failed',
          message: 'Something went wrong. Please try again',
        });
        datasetFailureSurfacedRef.current = true;
      }

      setIsDatasetPolling(false);

      return;
    }

    datasetFailureSurfacedRef.current = false;

    if (!isDatasetPolling) return;

    toast.dismiss(DATASET_TOAST_ID);

    if (status === DATASET_STATUSES.CREATED) {
      toast.success({
        title: 'Dataset recreated successfully',
        message: 'Your dataset is ready to use',
      });
    }

    setIsDatasetPolling(false);
  }, [datasetData?.status, isDatasetPolling]);

  useEffect(() => () => toast.dismiss(DATASET_TOAST_ID), []);

  const createModel = useCreateAidModel();
  const updateModel = useUpdateAidModel();
  const createDataset = useCreateAidModelDataset();
  const renameModel = useRenameAidModel({ userId: userId ?? '', clientId });

  const sourceTablesOptions = useMemo(() => {
    if (!sourceTables) return [];

    const options = sourceTables.map((table) => ({
      value: table.name,
      label: table.name,
    }));

    const savedTableName = datasetData?.sourceTableName;

    if (
      !isNewModel &&
      savedTableName &&
      !options.some((opt) => opt.value === savedTableName)
    ) {
      options.unshift({ value: savedTableName, label: savedTableName });
    }

    return options;
  }, [sourceTables, isNewModel, datasetData?.sourceTableName]);

  const buildSavedValues = useCallback(
    (model: AidModelDto, dataset: AidModelDatasetDto | undefined) => ({
      name: model.name || '',
      description: model.description || '',
      entryTerm: model.entryTerm ?? DEFAULT_ENTRY_TERM_YEAR,
      enrollmentType: model.enrollmentType || 'FIRST_YEAR',
      sourceTableName: dataset?.sourceTableName || '',
    }),
    []
  );

  useEffect(() => {
    isFormInitializedRef.current = false;
    setRecreateDataset(false);
  }, [modelId]);

  useEffect(() => {
    if (
      modelData &&
      !isNewModel &&
      (!isDatasetPolling || !isFormInitializedRef.current)
    ) {
      isFormInitializedRef.current = true;
      reset(buildSavedValues(modelData, datasetData));
    }
  }, [
    modelData,
    datasetData,
    isNewModel,
    reset,
    isDatasetPolling,
    buildSavedValues,
  ]);

  const handleRecreateDatasetChange = (checked: boolean): void => {
    setRecreateDataset(checked);

    if (!checked) {
      setValue('sourceTableName', datasetData?.sourceTableName || '', {
        shouldDirty: true,
      });
    }
  };

  const triggerDatasetCreation = async (
    targetModelId: string | number,
    sourceTableName: string
  ): Promise<void> => {
    await createDataset.mutateAsync({
      userId: userId ?? '',
      modelId: targetModelId,
      sourceTableName,
    });
  };

  const handleSave = async (formData: AidStudioFormValues): Promise<void> => {
    setIsSaving(true);

    try {
      const payload = {
        name: formData.name,
        description: formData.description || null,
        entryTerm: formData.entryTerm,
        enrollmentType:
          formData.enrollmentType as AidModelUpdateRequestEnrollmentType,
      };

      if (isNewModel) {
        const result = (await createModel.mutateAsync({
          userId: userId ?? '',
          clientId,
          data: payload,
        })) as AidModelDto;

        toast.success({
          title: MODEL_SAVED_TOAST_TITLE,
          message: `You've successfully created ${formData.name}`,
        });

        if (formData.sourceTableName && result.id != null) {
          try {
            await triggerDatasetCreation(result.id, formData.sourceTableName);
          } catch {
            toast.error({
              title: 'Dataset creation failed',
              message:
                'The model was saved, but dataset creation could not be started',
            });
          }
        }

        navigate(`${aidStudioBasePath}/${result.id}/${AID_STUDIO_STEP.FUNDS}`, {
          replace: true,
        });

        return;
      }

      await updateModel.mutateAsync({
        userId: userId ?? '',
        clientId,
        modelId: modelId ?? '',
        data: payload,
      });

      toast.success({
        title: MODEL_SAVED_TOAST_TITLE,
        message: `You've successfully updated ${formData.name}`,
      });

      if (recreateDataset && formData.sourceTableName && modelId != null) {
        try {
          await triggerDatasetCreation(modelId, formData.sourceTableName);
          setRecreateDataset(false);
        } catch {
          toast.error({
            title: 'Dataset recreation failed',
            message:
              'The model was saved, but dataset recreation could not be started',
          });
        }
      }

      reset(formData);
    } finally {
      setIsSaving(false);
    }
  };

  const handleRename = useCallback(
    async (name: string): Promise<void> => {
      if (modelId == null) return;

      try {
        await renameModel.mutateAsync({ modelId, data: { name } });
        setValue('name', name);
        toast.success({
          title: MODEL_SAVED_TOAST_TITLE,
          message: `The model is now named ${name}`,
        });
      } catch (error) {
        handleAidModelError(error, 'Failed to rename model');
      }
    },
    [modelId, renameModel, setValue]
  );

  const handleCancel = (): void => {
    navigate(aidStudioBasePath);
  };

  const handleDiscard = (): void => {
    setRecreateDataset(false);

    if (modelData && !isNewModel) {
      reset(buildSavedValues(modelData, datasetData));

      return;
    }

    reset(DEFAULT_VALUES);
  };

  return {
    control,
    errors,
    isValid,
    isDirty,
    handleSubmit,
    getValues,
    isNewModel,
    isSaving,
    isLoading: !isNewModel && modelLoading,
    isError: modelError,
    errorMessage: modelErrorMessage,
    modelData,
    datasetData,
    sourceTablesOptions,
    sourceTablesLoading,
    sourceTablesFetching,
    recreateDataset,
    handleRecreateDatasetChange,
    handleSave,
    handleRename,
    handleCancel,
    handleDiscard,
  };
};
